import { useEffect, useState } from "react";
import { MapContainer, Polyline } from "react-leaflet";
import MapTiles from "../components/MapTiles";
import { toast } from "sonner";
import { Flame as FlameIcon, Zap, Footprints, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { fmtDistance, fmtDuration, fmtPace, fmtDateTime } from "../lib/format";

function bounds(route) {
  const lats = route.map((p) => p.lat);
  const lngs = route.map((p) => p.lng);
  return [
    [Math.min(...lats), Math.min(...lngs)],
    [Math.max(...lats), Math.max(...lngs)],
  ];
}

export default function Feed() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/social/feed").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const cheer = async (id) => {
    try {
      const { data } = await api.post(`/runs/${id}/cheer`);
      setItems((prev) => prev.map((r) => (r.id === id ? { ...r, cheered: data.cheered, cheers: data.cheers } : r)));
    } catch {
      toast.error("Aktion fehlgeschlagen");
    }
  };

  return (
    <div className="max-w-lg mx-auto px-5 pt-safe">
      <h1 className="font-display font-black text-4xl tracking-tighter mb-6">Feed</h1>

      {loading ? (
        <div className="text-neutral-500 text-sm">Laden...</div>
      ) : items.length === 0 ? (
        <div className="bg-[#121212] border border-white/10 rounded-3xl p-8 text-center" data-testid="feed-empty">
          <Users className="w-10 h-10 text-neutral-600 mx-auto mb-3" />
          <p className="font-display font-black text-xl tracking-tighter mb-1">Noch nichts los</p>
          <p className="text-neutral-400 text-sm mb-4">Füge Freunde hinzu, um ihre Läufe zu sehen und sie anzufeuern.</p>
          <button onClick={() => navigate("/friends")} className="text-[#CCFF00] font-bold text-sm" data-testid="feed-add-friends">
            Freunde hinzufügen →
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="feed-list">
          {items.map((r) => {
            const hasRoute = r.route && r.route.length > 1;
            return (
              <div key={r.id} data-testid={`feed-item-${r.id}`} className="bg-[#121212] border border-white/10 rounded-2xl overflow-hidden">
                <div className="flex items-center gap-3 p-4">
                  <div className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center font-display font-black shrink-0">
                    {r.user_name?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate flex items-center gap-1.5">
                      {r.user_name} {r.is_me && <span className="text-xs text-[#CCFF00]">(Du)</span>}
                      {r.type === "walk" ? <Footprints className="w-3.5 h-3.5 text-neutral-500" /> : <Zap className="w-3.5 h-3.5 text-[#CCFF00]" />}
                    </div>
                    <div className="text-xs text-neutral-500">{fmtDateTime(r.started_at)}</div>
                  </div>
                </div>

                {hasRoute && (
                  <div className="h-40 dark-map border-y border-white/10">
                    <MapContainer bounds={bounds(r.route)} zoomControl={false} attributionControl={false} dragging={false} scrollWheelZoom={false} doubleClickZoom={false} style={{ height: "100%", width: "100%", pointerEvents: "none" }}>
                      <MapTiles />
                      <Polyline positions={r.route.map((p) => [p.lat, p.lng])} pathOptions={{ color: "#CCFF00", weight: 4 }} />
                    </MapContainer>
                  </div>
                )}

                <div className="flex items-center gap-6 p-4">
                  <div>
                    <div className="data-label">Distanz</div>
                    <div className="font-display font-black text-xl tracking-tighter text-[#CCFF00]">{fmtDistance(r.distance_m)}<span className="text-xs text-neutral-500 ml-1">km</span></div>
                  </div>
                  <div>
                    <div className="data-label">Zeit</div>
                    <div className="font-display font-black text-xl tracking-tighter">{fmtDuration(r.duration_s)}</div>
                  </div>
                  <div>
                    <div className="data-label">Pace</div>
                    <div className="font-display font-black text-xl tracking-tighter">{fmtPace(r.pace_min_km)}</div>
                  </div>
                  <button
                    onClick={() => cheer(r.id)}
                    data-testid={`cheer-${r.id}`}
                    className={`ml-auto flex items-center gap-1.5 rounded-full px-4 py-2 border transition-colors active:scale-95 ${
                      r.cheered ? "bg-[#FF3366]/20 border-[#FF3366]/50 text-[#FF3366]" : "bg-[#1A1A1A] border-white/10 text-neutral-300"
                    }`}
                  >
                    <FlameIcon className="w-4 h-4" fill={r.cheered ? "#FF3366" : "none"} />
                    <span className="text-sm font-bold">{r.cheers}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
